import { Users } from "lucide-react";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { useChatStore } from "@/features/chat/store/chat-store";
import type { Conversation } from "@/features/chat/types";
import { conversationTitle, formatTime, initials, otherMember } from "@/features/chat/utils";
import { PresenceDot } from "@/features/chat/components/presence-dot";
import { cn } from "@/lib/utils";

interface ConversationListProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  /** Group rows under "Groups" / "Direct messages" subheaders (unfiltered view). */
  showSections?: boolean;
}

function ConversationRow({
  conversation,
  active,
  onSelect,
}: {
  conversation: Conversation;
  active: boolean;
  onSelect: (id: string) => void;
}) {
  const meId = useAuthStore((s) => s.user?.id ?? null);
  const myName = useAuthStore((s) => s.user?.name ?? null);
  const isOnline = useChatStore((s) => s.isOnline);
  const title = conversationTitle(conversation, meId);
  const other = otherMember(conversation, meId);
  const isGroup = conversation.kind === "group";
  const online = !isGroup && other ? isOnline(other.id) : false;
  const unread = conversation.unreadCount > 0;
  const fromMe =
    conversation.lastMessage != null &&
    myName != null &&
    conversation.lastMessage.senderName === myName;

  return (
    <button
      type="button"
      onClick={() => onSelect(conversation.id)}
      aria-current={active ? "true" : undefined}
      className={cn(
        "flex w-full items-center gap-3 rounded-[14px] p-2.5 text-left transition-colors",
        active ? "bg-accent" : "hover:bg-muted",
      )}
    >
      <div className="relative shrink-0">
        <span
          className={cn(
            "font-heading flex size-[42px] items-center justify-center rounded-sm text-sm font-bold",
            isGroup ? "bg-brand-panel text-white" : "bg-accent text-info",
          )}
        >
          {isGroup ? <Users className="size-[18px]" /> : initials(title)}
        </span>
        {!isGroup && (
          <PresenceDot online={online} className="absolute -right-0.5 -bottom-0.5 size-3" />
        )}
      </div>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold">{title}</span>
          <span
            className={cn(
              "shrink-0 text-[11px] tabular-nums",
              unread ? "text-info font-semibold" : "text-muted-foreground",
            )}
          >
            {formatTime(conversation.lastMessageAt)}
          </span>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <span
            className={cn(
              "truncate text-xs",
              unread ? "text-foreground font-medium" : "text-muted-foreground",
            )}
          >
            {conversation.lastMessage ? (
              <>
                {fromMe && <span className="text-muted-foreground">You: </span>}
                {conversation.lastMessage.body}
              </>
            ) : (
              "No messages yet"
            )}
          </span>
          {unread && (
            <span className="bg-primary flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted-foreground px-2.5 pt-2.5 pb-1 text-[11px] font-semibold tracking-[0.1em] uppercase">
      {children}
    </p>
  );
}

export function ConversationList({
  conversations,
  activeId,
  onSelect,
  showSections = false,
}: ConversationListProps) {
  if (conversations.length === 0) {
    return (
      <p className="text-muted-foreground px-3 py-8 text-center text-sm">
        No conversations yet. Start one with a teammate.
      </p>
    );
  }

  const row = (c: Conversation) => (
    <ConversationRow
      key={c.id}
      conversation={c}
      active={c.id === activeId}
      onSelect={onSelect}
    />
  );

  if (showSections) {
    const groups = conversations.filter((c) => c.kind === "group");
    const directs = conversations.filter((c) => c.kind === "direct");
    return (
      <div className="flex flex-col gap-0.5">
        {groups.length > 0 && (
          <>
            <SectionLabel>Groups</SectionLabel>
            {groups.map(row)}
          </>
        )}
        {directs.length > 0 && (
          <>
            <SectionLabel>Direct messages</SectionLabel>
            {directs.map(row)}
          </>
        )}
      </div>
    );
  }

  return <div className="flex flex-col gap-0.5">{conversations.map(row)}</div>;
}
